import { useQuery } from '@tanstack/react-query'
import Processing from 'ext-ui/components/Processing/Processing'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PlanPeriod } from 'src/app/Onboarding/DynamicPricingTierCard'
import DynamicSaasOnboardingPricingTiers from 'src/app/Onboarding/DynamicSaasOnboardingPricingTiers'
import PricingFeaturesComparisonTable from 'src/app/Onboarding/PricingFeaturesComparisonTable'
import PricingPageHero from 'src/app/Onboarding/PricingPageHero'
import { createPricingPresentation } from 'src/app/Onboarding/pricingPresentation'
import PromotionSavingsHero, {
  PromotionBillingCycle,
} from 'src/app/Onboarding/PromotionSavingsHero'
import { ExtendLocales } from 'src/enums'
import { CurrencyFormatter } from 'src/localization/currencyFormatter'
import { TranslateFn } from 'src/localization/translate'
import { pricingPlanQueries } from 'src/queries/organizations/pricingPlans/pricingPlans.queries'
import { isProPricingPlan } from 'src/queries/organizations/pricingPlans/pricingPlans.utils'

import BinCard, { BinCardBankProfile } from './components/BinCard/BinCard'
import {
  MARCOM_CARD_ART_URLS,
  resolveMarcomPartnerPresentation,
} from './marcomPartnerPresentation'
import style from './MarcomPricingPage.style'

const DEFAULT_MARCOM_BIN = '345000'
const ENTERPRISE_CONTACT_URL = 'https://www.paywithextend.com/support-request'

export interface MarcomPricingPageContentProps {
  currencyFormatter: CurrencyFormatter
  locale?: ExtendLocales
  translate: TranslateFn
}

export function MarcomPricingPageContent(
  props: MarcomPricingPageContentProps,
): ReactElement {
  const {
    currencyFormatter,
    locale = ExtendLocales.enUS,
    translate,
  } = props
  const presentation = useMemo(
    () => createPricingPresentation(translate, locale),
    [locale, translate],
  )
  const [bin, setBin] = useState('')
  const [promotionBillingCycle, setPromotionBillingCycle] =
    useState<PromotionBillingCycle>('YEARLY')
  const resolvedBin = bin.length === 6 ? bin : DEFAULT_MARCOM_BIN
  const {
    data: pricingPlans,
    isLoading,
    isPlaceholderData,
    isError,
  } = useQuery(pricingPlanQueries.lookup(resolvedBin))
  const hasLookupError = isError && bin.length === 6
  const hasCurrentBinResult =
    bin.length === 6 && !isPlaceholderData && !hasLookupError

  useEffect(() => {
    MARCOM_CARD_ART_URLS.forEach((url) => {
      const image = new Image()
      image.src = url
    })
  }, [])

  const sortedPlans = useMemo(() => {
    if (!pricingPlans?.plans) return []
    return [...pricingPlans.plans].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    )
  }, [pricingPlans?.plans])

  const partnerPresentation = useMemo(
    () =>
      hasCurrentBinResult
        ? resolveMarcomPartnerPresentation(pricingPlans?.partnerName)
        : undefined,
    [hasCurrentBinResult, pricingPlans?.partnerName],
  )
  const activePromotion = hasCurrentBinResult
    ? (pricingPlans?.promotion ?? undefined)
    : undefined

  const bank = useMemo<BinCardBankProfile | null>(() => {
    if (!partnerPresentation) {
      return null
    }

    const promotionCardArt = activePromotion?.cardArtUrl?.trim()
    const genericCardArt = partnerPresentation.cardArtUrl

    return {
      cardArt: promotionCardArt || genericCardArt,
      fallbackCardArt:
        promotionCardArt && promotionCardArt !== genericCardArt
          ? genericCardArt
          : undefined,
      cardNetwork: partnerPresentation.partner,
    }
  }, [activePromotion?.cardArtUrl, partnerPresentation])

  const isNonExtendIssuer = Boolean(partnerPresentation)
  const showVirtualCardAlert =
    hasCurrentBinResult && Boolean(pricingPlans?.showVCUnavailableAlert)
  const getPlanCtaAction = ({
    plan,
    planPeriod,
  }: {
    plan: (typeof sortedPlans)[number]
    planPeriod: PlanPeriod
  }) => {
    const href =
      planPeriod === 'YEARLY' ? plan.ctaUrls?.yearly : plan.ctaUrls?.monthly

    return {
      href:
        href ??
        (plan.ctaUrls === null && plan.id.includes('enterprise')
          ? ENTERPRISE_CONTACT_URL
          : undefined),
      rel: 'noopener noreferrer',
      target: '_blank',
    }
  }

  return (
    <div css={style} className="MarcomPricingPage">
      <div className="SaasOnboardingPricingTiers-container flex-column justify-center align-center gap-10">
        {isLoading ? (
          <Processing />
        ) : (
          <>
            <PricingPageHero
              className="MarcomPricingPage-hero"
              translate={translate}
              partner={partnerPresentation?.partner}
            />
            <div className="MarcomPricingPage-binCard">
              <BinCard
                bank={bank}
                onBinChange={setBin}
                translate={translate}
                hasError={hasLookupError}
                isLoading={isPlaceholderData}
              />
            </div>
            {activePromotion && (
              <PromotionSavingsHero
                className="MarcomPricingPage-promotionHero"
                promotion={activePromotion}
                billingCycle={promotionBillingCycle}
                currencyFormatter={currencyFormatter}
                presentation={presentation}
              />
            )}
            <DynamicSaasOnboardingPricingTiers
              pricingPlans={sortedPlans}
              promotion={activePromotion}
              showVirtualCardAlert={showVirtualCardAlert}
              isNonExtendIssuer={isNonExtendIssuer}
              currencyFormatter={currencyFormatter}
              translate={translate}
              getPlanCtaAction={getPlanCtaAction}
              onPlanPeriodChange={(plan, planPeriod): void => {
                if (!isProPricingPlan(plan)) return
                setPromotionBillingCycle(planPeriod)
              }}
              presentation={presentation}
            />
            <div className="MarcomPricingPage-featuresComparisonTable">
              <PricingFeaturesComparisonTable
                pricingPlans={sortedPlans}
                translate={translate}
                presentation={presentation}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
